import { MainMenuContent } from 'components/menu/main_menu_content'
import { ProfileDetails } from 'components/menu/profile_details'
import { OnboardingButton } from 'components/onboarding/onboarding_button'
import {
  TimelineAction,
  timelineActionContainerStyles,
} from 'components/onboarding/timeline_action'
import { Avatar } from 'components/shared/avatar'
import { useNavAndClearSelected } from 'hooks/app_location/use_nav_and_clear_selected'
import { NewTopicIcon, SpeakeasyLogo } from 'images'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  getCurrentHandle,
  isConfirmedSelector,
} from 'reduxes/slices/current/selectors'
import { getRoutes } from 'reduxes/slices/routes/selectors'
import { useSelectorDelegateStore } from 'reduxes/stores/root_store/hooks'

import { css } from 'design'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import { TimelineContent } from './timeline_content'

export const PersonalChatActions = () => {
  const [updatingProfile, setUpdatingProfile] = useState<boolean>(false)
  const currentHandle = useSelector(getCurrentHandle)
  const isConfirmed = useSelectorDelegateStore(isConfirmedSelector)

  const routes: any = useSelectorDelegateStore(getRoutes)
  const navAndClearSelected = useNavAndClearSelected()

  const navToNewTopicWizard = useCallback(() => {
    navAndClearSelected(routes.admin_app_group_chats_new.path)
  }, [navAndClearSelected, routes.admin_app_group_chats_new.path])

  const navToDirectChatWizard = useCallback(() => {
    navAndClearSelected(routes.admin_app_direct_chats_new.path)
  }, [navAndClearSelected, routes.admin_app_direct_chats_new.path])

  return (
    <TimelineContent
      avatar={
        <div className={avatarStyles()}>
          <SpeakeasyLogo />
        </div>
      }
      timelineInfo={<span className={labelStyles()}>Speakeasy</span>}
      className={cn(containerStyles(), 'timeline__personal__actions')}
    >
      {updatingProfile && (
        <>
          <Stack
            as="section"
            gap="2"
            className={cn(
              timelineActionContainerStyles(),
              profileDetailsContainerStyles(),
            )}
          >
            <MainMenuContent showAvatar isOpen />
            <ProfileDetails qualifier="timeline" />
          </Stack>
          <OnboardingButton
            onClick={() => setUpdatingProfile(false)}
            className={profileDetailsDoneButtonStyles()}
          >
            Done
          </OnboardingButton>
        </>
      )}
      {!updatingProfile && !isConfirmed && (
        <TimelineAction
          className={unconfirmedWelcomeMessageStyles()}
          header="Welcome, once your email is confirmed we can get started!"
        />
      )}
      {!updatingProfile && isConfirmed && (
        <TimelineAction header="Welcome! Try one of these actions below to get started 🎉">
          <Stack gap="2">
            <OnboardingButton onClick={() => setUpdatingProfile(true)}>
              <Avatar handle={currentHandle} hideOnlineIndicator size={18} />
              <span>Add your name and picture</span>
            </OnboardingButton>

            <OnboardingButton onClick={navToNewTopicWizard}>
              <NewTopicIcon />
              <span>Create a new group topic</span>
            </OnboardingButton>

            <OnboardingButton onClick={navToDirectChatWizard}>
              <NewTopicIcon />
              <span>Message someone privately</span>
            </OnboardingButton>
          </Stack>
        </TimelineAction>
      )}
    </TimelineContent>
  )
}

const containerStyles = css({
  padding: '$3 $4',

  '@hover': {
    '&:hover': {
      backgroundColor: '$grey50',
    },
  },
})

const avatarStyles = css({
  width: 40,
  height: 40,
  borderRadius: '$2',
  backgroundColor: 'white',
  '> *': {
    width: 'inherit',
    height: 'inherit',
  },
})

const labelStyles = css({
  fontSize: '$3',
  color: '$grey900',
  fontWeight: 'bold',
})

const profileDetailsContainerStyles = css({
  backgroundColor: '$backgroundSecondary',
  padding: '$3',
})

const profileDetailsDoneButtonStyles = css({
  maxWidth: 350,
})

const unconfirmedWelcomeMessageStyles = css({
  maxWidth: 'unset !important',
  width: 'max-content',
})
